import React, { useMemo } from 'react'
import { numberFormat } from 'shared/util'

import {
  Stack, Typography, Tooltip,
  Table, TableContainer, TableBody, TableCell, TableHead, TableRow
} from '@mui/material'

import { TableVirtuoso } from 'react-virtuoso'

import { RectangleRounded as DpcMarkIcon } from '@mui/icons-material'

import { SisetusDpcResult } from 'interface/dpc/types'
import { SelectedDpc } from '../types'

type DpcPrefectureTableProps = {
  results: SisetusDpcResult[]
  selectedDpcList: SelectedDpc[]
  calcTotalCase: (
    result: SisetusDpcResult,
    dpcCode: string
  ) => number | undefined
}
export const DpcPrefectureTable = ({
  results,
  selectedDpcList,
  calcTotalCase,
}: DpcPrefectureTableProps) => {
  const prefectures = useMemo(
    () => {
      const prefecturesMap: { [prefectureCode: string]: {
        prefectureCode: string
        prefectureName: string
        result: { [dpcCode: string]: number }
      }} = {}
      for(const result of results) {
        const prefectureCode = result.sisetu.juusyoKenCode
        if (!prefectureCode) continue
        let prefecture = prefecturesMap[prefectureCode]
        if (!prefecture) {
          prefecture = {
            prefectureCode,
            prefectureName: result.sisetu.juusyoKenName ?? '',
            result: {}
          }
          prefecturesMap[prefectureCode] = prefecture
        }
        for(const { dpcCode } of selectedDpcList) {
          const value = calcTotalCase(result, dpcCode)
          if (value === undefined || value === null) continue
          prefecture.result[dpcCode] = (prefecture.result[dpcCode] ?? 0) + value
        }
      }
      const prefectures = Object.values(prefecturesMap)
      prefectures.sort((a, b) => {
        for(const { dpcCode } of selectedDpcList) {
          const valueA = a.result[dpcCode] ?? -1
          const valueB = b.result[dpcCode] ?? -1
          if (valueA === valueB) continue
          return valueB - valueA
        }
        return 0
      })
      return prefectures
    },
    [results, selectedDpcList, calcTotalCase]
  )
  return (
    <TableVirtuoso
      components={{
        Scroller: React.forwardRef(
          function VirtuosoTableContainerImpliment(props, ref) {
            return <TableContainer {...props} ref={ref} />
          }
        ),
        Table: (props) => (
          <Table
            {...props}
            size="small"
            style={{
              borderCollapse: 'separate',
              width: '100%'
            }}
          />
        ),
        TableHead: React.forwardRef(
          function VirtusoTableHeadImpliment(props, ref) {
            return (
              <TableHead {...props} ref={ref} sx={{ background: 'white' }} />
            )
          }
        ),
        TableRow: (props) => (
          <TableRow {...props}/>
        ),
        TableBody: React.forwardRef(
          function VirtusoTableBodyImpliment(props, ref) {
            return <TableBody {...props} ref={ref} />
          }
        ),
      }}
      style={{
        width: '100%',
        height: '100%',
      }}
      data={prefectures}
      fixedHeaderContent={() => (
        <TableRow>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>#</TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>都道府県</TableCell>

          {selectedDpcList.map((dpc, index) => (
            <TableCell
              sx={{ whiteSpace: 'nowrap' }}
              key={`table_${dpc.dpcCode}_${index}`}
            >
              <Tooltip title={dpc.diseaseName ?? `column-${index}`}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <DpcMarkIcon fontSize="small" sx={{ color: dpc.color }} />
                  <Typography variant='body2' whiteSpace='nowrap' textOverflow='ellipsis' overflow='hidden' maxWidth='5rem'>
                    {dpc.diseaseName ?? `column-${index}`}
                  </Typography>
                </Stack>
              </Tooltip>
            </TableCell>
          ))}
          <TableCell width="100%" padding='none'/>
        </TableRow>
      )}
      itemContent={(index, { prefectureCode, prefectureName, result }) => (
        <>
          <TableCell align='right'>{index + 1}</TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>
            {prefectureName}
          </TableCell>

          {selectedDpcList.map((dpc, index) => {
            const value = result[dpc.dpcCode]
            return (value === undefined || value === null) ? (
              <TableCell
                sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}
                key={`${prefectureCode}_${dpc.dpcCode}_${index}`}
              >
                -
              </TableCell>
            ) : (
              <TableCell
                sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}
                key={`${prefectureCode}_${dpc.dpcCode}_${index}`}
              >
                {value ? `${numberFormat(value)} 件` : ''}
              </TableCell>
            )
          })}
          <TableCell width="100%" padding='none'/>
        </>
      )}
    />
  )
}
